/**
 * AiHealthSection — DevTools zakładka „AI Health" (2026-02).
 *
 * Agreguje dane z AI Provider Log (backend `/api/dev-tools/ai-health/*`).
 * NIE tworzy nowego systemu telemetrycznego — używa istniejącej kolekcji
 * `ai_provider_logs`.
 *
 * Kluczowe cechy:
 *  - Per-agent statystyki (calls, success_rate, latency, tokens, cost).
 *  - Liczniki błędów wg typów (finish_reason=length / timeout / parser_error
 *    / provider_error / rate_limit / unknown_recommendation / disabled_provider
 *    / itd. — nazwy pochodzą z rzeczywistych danych, nie hardkodowane).
 *  - Alerty progowe (24h) — konfigurowalne przez ENV.
 *  - Tabela ostatnich błędów z linkiem do szczegółu (response_id → link do
 *    istniejącego AI Provider Log w Dev Tools).
 *  - Szczegół agenta: top 10 błędów, największy request/response/cost/latency.
 *
 * Audyt: `/app/docs/AI_HEALTH_DEVTOOLS.md`.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, RefreshCw, Loader2, Server, Zap,
  Timer, XCircle, Filter, ChevronRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Palety dla error types (fallback: gray) ─────────────────────────────
const ERROR_COLORS = {
  timeout: "bg-orange-100 text-orange-700 ring-orange-300",
  parser_error: "bg-red-100 text-red-700 ring-red-300",
  invalid_json: "bg-red-100 text-red-700 ring-red-300",
  provider_error: "bg-red-100 text-red-700 ring-red-300",
  rate_limit: "bg-yellow-100 text-yellow-700 ring-yellow-300",
  unknown_recommendation: "bg-red-100 text-red-700 ring-red-300",
  disabled_provider: "bg-gray-100 text-gray-700 ring-gray-300",
  config: "bg-gray-100 text-gray-700 ring-gray-300",
  empty_response: "bg-red-100 text-red-700 ring-red-300",
  auth: "bg-red-100 text-red-700 ring-red-300",
};

const ErrorBadge = ({ type, count }) => {
  const cls = ERROR_COLORS[type] || "bg-gray-100 text-gray-700 ring-gray-300";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-mono ring-1 ${cls}`}
      data-testid={`ai-health-err-badge-${type}`}
    >
      {type}
      {count != null && <b className="ml-0.5">{count}</b>}
    </span>
  );
};

const fmt2 = (v) => (v == null ? "—" : Number(v).toFixed(2));
const fmt6 = (v) => (v == null ? "—" : Number(v).toFixed(6));
const fmtInt = (v) => (v == null ? "—" : Number(v).toLocaleString("pl-PL"));

// ── Agent card ──────────────────────────────────────────────────────────
const AgentCard = ({ agent, onSelect }) => {
  const successRate = agent.success_rate == null
    ? "—"
    : `${(agent.success_rate * 100).toFixed(1)}%`;
  const successColor = agent.success_rate == null ? "text-gray-400"
    : agent.success_rate >= 0.95 ? "text-green-600"
    : agent.success_rate >= 0.80 ? "text-yellow-600" : "text-red-600";
  const errTypes = Object.entries(agent.error_types || {});
  return (
    <Card
      onClick={() => onSelect?.(agent.agent_id)}
      className="cursor-pointer hover:shadow-md transition"
      data-testid={`ai-health-agent-card-${agent.agent_id}`}
    >
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-gray-900 truncate">{agent.agent_id}</h4>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </div>
            <p className="text-[11px] text-gray-500 font-mono">
              {(agent.providers || []).join(", ") || "—"}
            </p>
          </div>
          <div className={`text-right ${successColor}`}>
            <div className="text-2xl font-bold" data-testid={`ai-health-success-rate-${agent.agent_id}`}>
              {successRate}
            </div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500">success</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <div className="bg-gray-50 rounded p-1.5">
            <div className="font-bold text-gray-800">{fmtInt(agent.total_calls)}</div>
            <div className="text-[10px] text-gray-500">wywołań</div>
          </div>
          <div className="bg-red-50 rounded p-1.5">
            <div className="font-bold text-red-700" data-testid={`ai-health-error-count-${agent.agent_id}`}>
              {fmtInt(agent.error_count)}
            </div>
            <div className="text-[10px] text-red-600">błędów</div>
          </div>
          <div className="bg-blue-50 rounded p-1.5">
            <div className="font-bold text-blue-700">{fmt2(agent.avg_latency_ms)}ms</div>
            <div className="text-[10px] text-blue-600">avg latency</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 text-[11px] text-gray-600">
          <div>tokens in: <b>{fmt2(agent.avg_input_tokens)}</b></div>
          <div>tokens out: <b>{fmt2(agent.avg_output_tokens)}</b></div>
          <div>cost: <b>${fmt6(agent.total_cost_usd)}</b></div>
        </div>

        {errTypes.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1 border-t border-gray-100">
            {errTypes.map(([type, count]) => (
              <ErrorBadge key={type} type={type} count={count} />
            ))}
          </div>
        )}
        {(agent.finish_reasons?.length ?? 0) > 0 && (
          <div className="text-[11px] text-gray-500">
            finish_reasons: {Object.entries(agent.finish_reasons).map(([k, v]) => `${k}=${v}`).join(", ")}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Errors table ────────────────────────────────────────────────────────
const ErrorsTable = ({ rows, loading }) => {
  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400"/></div>;
  }
  if (!rows || rows.length === 0) {
    return <p className="text-sm text-gray-500 italic text-center py-6" data-testid="ai-health-errors-empty">Brak błędnych wywołań w wybranym zakresie.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="ai-health-errors-table">
        <thead className="border-b border-gray-200">
          <tr className="text-left text-gray-500 uppercase tracking-wide">
            <th className="py-2 pr-2">Data</th>
            <th className="pr-2">Agent</th>
            <th className="pr-2">Provider</th>
            <th className="pr-2">Model</th>
            <th className="pr-2">Typ błędu</th>
            <th className="pr-2">First Run</th>
            <th className="pr-2">Finish</th>
            <th className="pr-2 text-right">In</th>
            <th className="pr-2 text-right">Out</th>
            <th className="pr-2 text-right">Lat (ms)</th>
            <th className="pr-2">Response ID</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={i}
              className="border-b border-gray-100 hover:bg-gray-50"
              data-testid={`ai-health-error-row-${i}`}
            >
              <td className="py-1.5 pr-2 whitespace-nowrap text-gray-700 font-mono">
                {String(r.created_at || "").slice(0, 19).replace("T", " ")}
              </td>
              <td className="pr-2 font-mono text-gray-800">{r.agent_id}</td>
              <td className="pr-2 font-mono text-gray-600">{r.provider}</td>
              <td className="pr-2 font-mono text-gray-600">{r.model}</td>
              <td className="pr-2"><ErrorBadge type={r.error_type || "unknown"} /></td>
              <td className="pr-2 text-center">
                {r.is_first_run ? (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700 ring-1 ring-blue-300">
                    FIRST
                  </span>
                ) : (
                  <span className="text-gray-400 text-[10px]">—</span>
                )}
              </td>
              <td className="pr-2 font-mono text-gray-600">{r.finish_reason || "—"}</td>
              <td className="pr-2 text-right font-mono">{r.tokens?.input ?? "—"}</td>
              <td className="pr-2 text-right font-mono">{r.tokens?.output ?? "—"}</td>
              <td className="pr-2 text-right font-mono">{r.latency_ms ?? "—"}</td>
              <td className="pr-2 font-mono text-gray-500 break-all max-w-[180px] truncate" title={r.response_id}>
                {r.response_id || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Alerts banner ───────────────────────────────────────────────────────
const AlertsBanner = ({ alerts }) => {
  if (!alerts || alerts.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="ai-health-alerts">
      {alerts.map((a, i) => (
        <div
          key={i}
          className={`rounded-lg p-3 text-sm border flex items-start gap-2 ${
            a.severity === "warning"
              ? "bg-yellow-50 border-yellow-300 text-yellow-900"
              : "bg-red-50 border-red-300 text-red-900"
          }`}
          data-testid={`ai-health-alert-${a.code}`}
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-xs uppercase tracking-wide">{a.code}</div>
            <div>{a.message}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Main section ────────────────────────────────────────────────────────
export default function AiHealthSection() {
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [errors, setErrors] = useState([]);
  const [errorsTotal, setErrorsTotal] = useState(0);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingErr, setLoadingErr] = useState(false);

  // Filtry.
  const [fltAgent, setFltAgent] = useState("all");
  const [fltProvider, setFltProvider] = useState("all");
  const [fltErrorType, setFltErrorType] = useState("all");
  const [fltFirstRun, setFltFirstRun] = useState("all");
  const [fltFrom, setFltFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  });
  const [fltTo, setFltTo] = useState(() => new Date().toISOString().slice(0, 10));

  const filters = useMemo(() => ({
    from: fltFrom ? `${fltFrom}T00:00:00Z` : undefined,
    to: fltTo ? `${fltTo}T23:59:59Z` : undefined,
    agent_id: fltAgent !== "all" ? fltAgent : undefined,
    provider: fltProvider !== "all" ? fltProvider : undefined,
  }), [fltFrom, fltTo, fltAgent, fltProvider]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, ag] = await Promise.all([
        axios.get(`${API}/dev-tools/ai-health/summary`, { params: filters, withCredentials: true }),
        axios.get(`${API}/dev-tools/ai-health/alerts`, { withCredentials: true }),
        axios.get(`${API}/dev-tools/ai-health/agents`, { withCredentials: true }),
      ]);
      setSummary(s.data);
      setAlerts(a.data.alerts || []);
      setAgents(ag.data.agents || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Wymagane uprawnienia administratora.");
      } else {
        toast.error("Błąd pobierania AI Health.");
      }
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchErrors = useCallback(async () => {
    setLoadingErr(true);
    try {
      const r = await axios.get(`${API}/dev-tools/ai-health/errors`, {
        params: {
          ...filters,
          error_type: fltErrorType !== "all" ? fltErrorType : undefined,
          is_first_run: fltFirstRun !== "all" ? fltFirstRun : undefined,
          limit: 50,
        },
        withCredentials: true,
      });
      setErrors(r.data.rows || []);
      setErrorsTotal(r.data.total || 0);
    } catch { /* toast obsłużony w fetchAll */ }
    setLoadingErr(false);
  }, [filters, fltErrorType, fltFirstRun]);

  const fetchDetail = useCallback(async (agentId) => {
    if (!agentId) { setDetail(null); return; }
    try {
      const r = await axios.get(`${API}/dev-tools/ai-health/agent/${agentId}`, {
        params: { from: filters.from, to: filters.to },
        withCredentials: true,
      });
      setDetail(r.data);
    } catch {
      toast.error("Błąd pobierania szczegółu agenta.");
    }
  }, [filters]);

  useEffect(() => { fetchAll(); fetchErrors(); }, [fetchAll, fetchErrors]);
  useEffect(() => { fetchDetail(selectedAgent); }, [selectedAgent, fetchDetail]);

  // Dostępne providery + typy błędów do filtru (dynamiczne z danych).
  const providerOpts = useMemo(() => {
    const s = new Set();
    (summary?.agents || []).forEach((a) => (a.providers || []).forEach((p) => s.add(p)));
    return Array.from(s);
  }, [summary]);
  const errorTypeOpts = useMemo(() => {
    const s = new Set();
    (summary?.agents || []).forEach((a) => Object.keys(a.error_types || {}).forEach((t) => s.add(t)));
    return Array.from(s);
  }, [summary]);

  return (
    <div className="space-y-4" data-testid="ai-health-section">
      {/* Nagłówek */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-blue-50">
            <Activity className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">AI Health</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Kondycja LLM per-agent — dane z AI Provider Log. Widoczne wyłącznie dla administratora.
            </p>
          </div>
        </div>
        <Button
          variant="outline" size="sm"
          onClick={() => { fetchAll(); fetchErrors(); if (selectedAgent) fetchDetail(selectedAgent); }}
          disabled={loading}
          data-testid="ai-health-refresh-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </div>

      {/* Alerty */}
      <AlertsBanner alerts={alerts} />

      {/* Filtry */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-gray-500" />
            <h3 className="text-sm font-semibold text-gray-800">Filtry</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 text-xs">
            <div>
              <Label className="text-[11px] text-gray-500">Od</Label>
              <Input type="date" value={fltFrom} onChange={(e) => setFltFrom(e.target.value)}
                     data-testid="ai-health-filter-from" />
            </div>
            <div>
              <Label className="text-[11px] text-gray-500">Do</Label>
              <Input type="date" value={fltTo} onChange={(e) => setFltTo(e.target.value)}
                     data-testid="ai-health-filter-to" />
            </div>
            <div>
              <Label className="text-[11px] text-gray-500">Agent</Label>
              <Select value={fltAgent} onValueChange={setFltAgent}>
                <SelectTrigger data-testid="ai-health-filter-agent"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Wszyscy</SelectItem>
                  {agents.map((a) => (
                    <SelectItem key={a.agent_id} value={a.agent_id}>{a.agent_id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] text-gray-500">Provider</Label>
              <Select value={fltProvider} onValueChange={setFltProvider}>
                <SelectTrigger data-testid="ai-health-filter-provider"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Wszystkie</SelectItem>
                  {providerOpts.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] text-gray-500">Typ błędu</Label>
              <Select value={fltErrorType} onValueChange={setFltErrorType}>
                <SelectTrigger data-testid="ai-health-filter-error-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Wszystkie</SelectItem>
                  {errorTypeOpts.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] text-gray-500">First Run</Label>
              <Select value={fltFirstRun} onValueChange={setFltFirstRun}>
                <SelectTrigger data-testid="ai-health-filter-first-run"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Wszystkie</SelectItem>
                  <SelectItem value="true">First Run</SelectItem>
                  <SelectItem value="false">Kolejny run</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Totals */}
      {summary && (
        <div className="grid grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-4 text-center">
              <Server className="w-5 h-5 mx-auto text-blue-600 mb-1" />
              <div className="text-2xl font-bold text-gray-900" data-testid="ai-health-totals-calls">
                {fmtInt(summary.totals.total_calls)}
              </div>
              <div className="text-xs text-gray-500">Wszystkie wywołania</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <XCircle className="w-5 h-5 mx-auto text-red-600 mb-1" />
              <div className="text-2xl font-bold text-red-700" data-testid="ai-health-totals-errors">
                {fmtInt(summary.totals.total_errors)}
              </div>
              <div className="text-xs text-gray-500">Błędy</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Zap className="w-5 h-5 mx-auto text-green-600 mb-1" />
              <div className="text-2xl font-bold text-green-700">
                {summary.totals.overall_success_rate != null
                  ? `${(summary.totals.overall_success_rate * 100).toFixed(1)}%`
                  : "—"}
              </div>
              <div className="text-xs text-gray-500">Success rate (całość)</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Per-agent cards */}
      {summary && summary.agents?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Agenci</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {summary.agents.map((a) => (
              <AgentCard key={a.agent_id} agent={a} onSelect={setSelectedAgent} />
            ))}
          </div>
        </div>
      )}

      {/* Szczegół agenta */}
      {selectedAgent && detail && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">
                Szczegół: <span className="font-mono">{selectedAgent}</span>
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedAgent(null)}
                      data-testid="ai-health-close-detail">Zamknij</Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div className="bg-gray-50 rounded p-2">
                <div className="text-[10px] text-gray-500">Total calls</div>
                <div className="text-lg font-bold">{fmtInt(detail.total_calls)}</div>
              </div>
              <div className="bg-gray-50 rounded p-2">
                <div className="text-[10px] text-gray-500">Avg latency</div>
                <div className="text-lg font-bold">{fmt2(detail.avg_latency_ms)}ms</div>
              </div>
              <div className="bg-gray-50 rounded p-2">
                <div className="text-[10px] text-gray-500">Largest req (tokens)</div>
                <div className="text-lg font-bold">{fmtInt(detail.biggest_request?.tokens?.input)}</div>
              </div>
              <div className="bg-gray-50 rounded p-2">
                <div className="text-[10px] text-gray-500">Largest resp (tokens)</div>
                <div className="text-lg font-bold">{fmtInt(detail.biggest_response?.tokens?.output)}</div>
              </div>
              <div className="bg-gray-50 rounded p-2">
                <div className="text-[10px] text-gray-500">Max tokens (total)</div>
                <div className="text-lg font-bold">{fmtInt(detail.biggest_tokens?.tokens?.total)}</div>
              </div>
              <div className="bg-gray-50 rounded p-2">
                <div className="text-[10px] text-gray-500">Max cost</div>
                <div className="text-lg font-bold">${fmt6(detail.biggest_cost?.estimated_cost_usd)}</div>
              </div>
              <div className="bg-gray-50 rounded p-2 col-span-2">
                <div className="text-[10px] text-gray-500">Longest latency</div>
                <div className="text-lg font-bold flex items-center gap-1">
                  <Timer className="w-4 h-4 text-orange-500" />
                  {fmtInt(detail.longest_latency?.latency_ms)}ms
                </div>
              </div>
            </div>

            {detail.top_errors?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-700 mb-1.5">Top 10 błędów</h4>
                <div className="space-y-1" data-testid="ai-health-top-errors">
                  {detail.top_errors.map((e, i) => (
                    <div key={i} className="flex items-center justify-between px-2 py-1.5 bg-red-50 rounded text-xs">
                      <div className="flex items-center gap-2 min-w-0">
                        <ErrorBadge type={e.error_type || "unknown"} />
                        {e.finish_reason && <span className="font-mono text-gray-500">finish={e.finish_reason}</span>}
                        <span className="font-mono text-gray-500 truncate">{e.provider}/{e.model}</span>
                      </div>
                      <div className="text-red-700 font-bold shrink-0">{e.count}×</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Errors table */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-800">Ostatnie błędy AI</h3>
            <span className="text-xs text-gray-500">łącznie: {fmtInt(errorsTotal)}</span>
          </div>
          <ErrorsTable rows={errors} loading={loadingErr} />
        </CardContent>
      </Card>
    </div>
  );
}
